/**
 * AOR scoping. The fleet, the health dashboard, the counts — everything is
 * partitioned by the Area of Responsibility the operator is in. A drone flying
 * in JAZIRA never shows up while you are working ANBAR WEST.
 *
 * Membership is by position (see positionInAor), recomputed on a slow interval
 * rather than the 10 Hz telemetry path: aircraft cross AOR boundaries rarely, so
 * a per-second sweep keeps the list stable without waking every row.
 */

import { useEffect, useState } from 'react';

import { AORS, aorById, aorForPosition, positionInAor } from '@/map/aors';
import { useDroneStore } from '@/stores/droneStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import type { DroneId } from '@/types/domain';

const SCOPE_REFRESH_MS = 1500;

function sameIds(a: DroneId[], b: DroneId[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/** Drone ids whose current position is inside the active AOR. */
export function useAorScopedIds(): DroneId[] {
  const aorId = useWorkspaceStore((state) => state.aor);
  const [ids, setIds] = useState<DroneId[]>([]);

  useEffect(() => {
    const aor = aorById(aorId);
    const compute = () => {
      const scoped: DroneId[] = [];
      for (const drone of useDroneStore.getState().drones.values()) {
        if (positionInAor(drone.scalars.lat, drone.scalars.lon, aor)) scoped.push(drone.droneId);
      }
      setIds((prev) => (sameIds(prev, scoped) ? prev : scoped));
    };
    compute();
    const timer = window.setInterval(compute, SCOPE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [aorId]);

  return ids;
}

/** Live track count in the active AOR. */
export function useAorScopedCount(): number {
  return useAorScopedIds().length;
}

/** Track counts keyed by AOR id, for the strategic globe pills. */
export function useAorCounts(): Record<string, number> {
  const [counts, setCounts] = useState<Record<string, number>>(() =>
    Object.fromEntries(AORS.map((a) => [a.id, 0])),
  );

  useEffect(() => {
    const compute = () => {
      const next: Record<string, number> = Object.fromEntries(AORS.map((a) => [a.id, 0]));
      for (const drone of useDroneStore.getState().drones.values()) {
        const aor = aorForPosition(drone.scalars.lat, drone.scalars.lon);
        if (aor) next[aor.id] = (next[aor.id] ?? 0) + 1;
      }
      setCounts((prev) => (AORS.every((a) => prev[a.id] === next[a.id]) ? prev : next));
    };
    compute();
    const timer = window.setInterval(compute, SCOPE_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  return counts;
}
